/*
  Shortlist: save products (hearts on cards and product pages), keep them in this browser, and turn the list into a
  lead. Markup: snippets/shortlist-button.liquid, snippets/shortlist-toast.liquid, the header heart, and the
  full-screen shortlist (snippets/shortlist-modal.liquid, drawn by assets/shortlist-modal.js through
  window.TAT.shortlist).

  - Saved items live in localStorage ('tat-shortlist'), as small snapshots (title, brand, link, image, price) plus the
    customer's own note on each ('note'). A general note lives in 'tat-shortlist-note'. Recently viewed product pages
    are kept the same way ('tat-recent').
  - A shared link (?shortlist=handle,handle on any page) rebuilds the list on another device and opens it.
  - Every contact form on the site carries the shortlist (with notes), the general note and recently viewed products
    as extra fields, so the team sees what the customer is weighing up whichever form they use. A line above the
    submit button says so and lets them leave the shortlist out of that message.
  - Pushes dataLayer events (add_to_wishlist, remove_from_wishlist, shortlist_share, shortlist_email,
    shortlist_enquire, shortlist_book) for Google Tag Manager to forward if wanted.
*/
const KEY = 'tat-shortlist';
const RECENT_KEY = 'tat-recent';
const NOTE_KEY = 'tat-shortlist-note';
const MAX = 24;
const MAX_RECENT = 10;

const read = (key) => {
  try {
    const list = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(list) ? list.filter((i) => i && i.handle && i.title) : [];
  } catch {
    return [];
  }
};
const write = (key, list) => {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    // Storage blocked (private mode): the list lasts for this page only.
  }
};
let items = read(KEY);
const has = (handle) => items.some((i) => i.handle === handle);
const track = (event, extra = {}) => {
  window.dataLayer = window.dataLayer || [];
  if (extra.ecommerce) window.dataLayer.push({ ecommerce: null }); // clear the previous ecommerce object (GTM)
  window.dataLayer.push({ event, ...extra });
};
const gaItems = (list) => list.map((i) => ({ item_id: i.handle, item_name: i.title, item_brand: i.vendor }));
const absolute = (url) => new URL(url, location.origin).href;
const snapshot = (el) => ({
  handle: el.dataset.handle,
  title: el.dataset.title,
  vendor: el.dataset.vendor || '',
  url: el.dataset.url,
  image: el.dataset.image || '',
  price: el.dataset.price || '',
  mode: el.dataset.mode || '',
  added: Date.now(),
});

function save(next, { silent } = {}) {
  items = next.slice(0, MAX);
  write(KEY, items);
  render();
  if (!silent) document.dispatchEvent(new CustomEvent('shortlist:change', { detail: { items } }));
}

// ---------------------------------------------------------------------------------------------------------------
// Drawing: buttons, counts and the note on enquiry forms
// ---------------------------------------------------------------------------------------------------------------
function render() {
  document.querySelectorAll('[data-shortlist-toggle]').forEach((btn) => {
    const saved = has(btn.dataset.handle);
    btn.setAttribute('aria-pressed', String(saved));
    const label = btn.querySelector('[data-shortlist-label]');
    if (label) {
      // Visible text: the accessible name starts with it (voice control users say what they see).
      label.textContent = saved ? 'Saved to your shortlist' : 'Save to your shortlist';
      btn.setAttribute('aria-label', `${label.textContent}: ${btn.dataset.title}`);
    } else {
      btn.setAttribute('aria-label', `${saved ? 'Remove' : 'Save'} ${btn.dataset.title} ${saved ? 'from' : 'to'} your shortlist`);
    }
  });
  document.querySelectorAll('[data-shortlist-count]').forEach((el) => {
    el.textContent = items.length;
    el.hidden = items.length === 0;
  });

  // Tell people their shortlist goes with any enquiry, and let them leave it out of this one message
  // (form.dataset.shortlistSkip; the saved shortlist itself is untouched).
  document.querySelectorAll('form[data-enquiry-form]:not([data-shortlist-form])').forEach((form) => {
    const submit = form.querySelector('[data-enquiry-submit]');
    if (!submit) return;
    let note = form.querySelector('[data-shortlist-note]');
    if (!note) {
      note = document.createElement('div');
      note.dataset.shortlistNote = '';
      note.className = 'flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-xl bg-cream px-3 py-2 text-xs text-ink/80';
      note.innerHTML =
        '<p class="min-w-0" data-shortlist-note-text></p>' +
        '<button type="button" class="shrink-0 font-semibold text-navy underline underline-offset-4 hover:no-underline" data-shortlist-note-toggle></button>';
      const row = submit.parentElement;
      row.parentElement.insertBefore(note, row);
    }
    note.hidden = items.length === 0;
    if (!items.length) return;
    const skip = form.dataset.shortlistSkip === 'true';
    const names = items.slice(0, 2).map((i) => i.title).join(' and ');
    const more = items.length > 2 ? ` and ${items.length - 2} more` : '';
    note.querySelector('[data-shortlist-note-text]').textContent = skip
      ? "Your shortlist won't be sent with this message."
      : `Your shortlist is included: ${names}${more}.`;
    note.querySelector('[data-shortlist-note-toggle]').textContent = skip ? 'Include it' : "Don't include";
  });
}

document.addEventListener('click', (e) => {
  const toggle = e.target.closest('[data-shortlist-note-toggle]');
  const form = toggle?.closest('form');
  if (!form) return;
  form.dataset.shortlistSkip = form.dataset.shortlistSkip === 'true' ? 'false' : 'true';
  render();
});

// ---------------------------------------------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------------------------------------------
let toastTimer;
function toast(text) {
  const el = document.querySelector('[data-shortlist-toast]');
  if (!el || document.getElementById('ShortlistModal')?.classList.contains('is-open')) return;
  el.querySelector('[data-shortlist-toast-text]').textContent = text;
  el.hidden = false;
  requestAnimationFrame(() => el.toggleAttribute('data-show', true));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    el.removeAttribute('data-show');
    setTimeout(() => (el.hidden = true), 300);
  }, 3500);
}

// ---------------------------------------------------------------------------------------------------------------
// Words for enquiries, email and sharing
// ---------------------------------------------------------------------------------------------------------------
const line = (i) => `${i.vendor && !i.title.toLowerCase().startsWith(i.vendor.toLowerCase()) ? `${i.vendor} ` : ''}${i.title}${i.price ? ` (${i.price})` : ''}`;
const shareUrl = () => `${location.origin}/?shortlist=${items.map((i) => encodeURIComponent(i.handle)).join(',')}`;
const openShortlist = () => document.getElementById('ShortlistModal')?.open?.();
const generalNote = () => {
  try {
    return (localStorage.getItem(NOTE_KEY) || '').trim();
  } catch {
    return '';
  }
};
function mailto() {
  const list = items.map((i) => `${line(i)}${i.note ? `\nMy note: ${i.note}` : ''}\n${absolute(i.url)}`).join('\n\n');
  const note = generalNote();
  const body = `My shortlist from The Audio Tailor:\n\n${list}${note ? `\n\nNotes: ${note}` : ''}\n\nOpen the whole list: ${shareUrl()}`;
  return `mailto:?subject=${encodeURIComponent('My hi-fi shortlist')}&body=${encodeURIComponent(body)}`;
}
const fieldValue = (list, withNotes = false) =>
  list.map((i) => `${line(i)} ${absolute(i.url)}${withNotes && i.note ? ` (note: ${i.note.replace(/\s+/g, ' ')})` : ''}`).join(' | ');

// ---------------------------------------------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------------------------------------------
document.addEventListener('click', async (e) => {
  const toggle = e.target.closest('[data-shortlist-toggle]');
  if (toggle) {
    e.preventDefault();
    e.stopPropagation();
    const item = snapshot(toggle);
    if (has(item.handle)) {
      save(items.filter((i) => i.handle !== item.handle));
      toast('Removed from your shortlist');
      track('remove_from_wishlist', { ecommerce: { items: gaItems([item]) } });
    } else {
      save([item, ...items]);
      toast('Saved to your shortlist');
      track('add_to_wishlist', { ecommerce: { items: gaItems([item]) } });
    }
    return;
  }

  const share = e.target.closest('[data-shortlist-share]');
  if (share) {
    const url = shareUrl();
    track('shortlist_share', { shortlist_count: items.length });
    if (navigator.share) {
      try {
        await navigator.share({ title: 'My hi-fi shortlist', text: 'My shortlist from The Audio Tailor', url });
      } catch {
        // Cancelled.
      }
      return;
    }
    const label = share.querySelector('[data-shortlist-share-label]');
    try {
      await navigator.clipboard.writeText(url);
      if (label) label.textContent = 'Link copied';
    } catch {
      window.prompt('Copy this link to your shortlist:', url);
    }
    setTimeout(() => label && (label.textContent = 'Share'), 2500);
    return;
  }

  const email = e.target.closest('[data-shortlist-email]');
  if (email) {
    email.href = mailto();
    track('shortlist_email', { shortlist_count: items.length });
  }
});

// Every contact form (enquiry pop-ups, contact page, trade-in forms) carries the shortlist and what they've looked at.
document.addEventListener(
  'submit',
  (e) => {
    const form = e.target;
    // Contact forms only (Shopify marks them form_type=contact); the newsletter form posts to /contact too.
    if (!(form instanceof HTMLFormElement) || form.querySelector('input[name="form_type"]')?.value !== 'contact') return;
    form.querySelectorAll('[data-shortlist-field]').forEach((el) => el.remove());
    const recent = read(RECENT_KEY).filter((r) => !has(r.handle)).slice(0, 6);
    const add = (name, value) => {
      if (!value) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `contact[${name}]`;
      input.value = value;
      input.dataset.shortlistField = '';
      form.append(input);
    };
    if (form.dataset.shortlistSkip !== 'true') {
      add('Shortlist', fieldValue(items, true));
      add('Shortlist notes', generalNote());
    }
    add('Recently viewed', fieldValue(recent));
  },
  true
);

// Other tabs and the theme editor.
window.addEventListener('storage', (e) => {
  if (e.key !== KEY) return;
  items = read(KEY);
  render();
});
document.addEventListener('shopify:section:load', render);
// Cards added later (load more, filters, section refreshes) need their hearts set.
new MutationObserver((records) => {
  if (records.some((r) => [...r.addedNodes].some((n) => n.nodeType === 1 && (n.matches?.('[data-shortlist-toggle]') || n.querySelector?.('[data-shortlist-toggle]'))))) render();
}).observe(document.body, { childList: true, subtree: true });

// ---------------------------------------------------------------------------------------------------------------
// Start: remember this product page, open a shared list, draw
// ---------------------------------------------------------------------------------------------------------------
const here = document.querySelector('[data-shortlist-toggle][data-context="product"]');
if (here) {
  const item = snapshot(here);
  write(RECENT_KEY, [item, ...read(RECENT_KEY).filter((r) => r.handle !== item.handle)].slice(0, MAX_RECENT));
}

const shared = new URL(location.href).searchParams.get('shortlist');
if (shared) {
  const handles = shared.split(',').map((h) => decodeURIComponent(h).trim()).filter((h) => /^[a-z0-9-]+$/.test(h)).slice(0, MAX);
  const url = new URL(location.href);
  url.searchParams.delete('shortlist');
  history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  const found = (
    await Promise.all(
      handles.map((h) =>
        fetch(`/products/${h}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    )
  ).filter(Boolean);
  const fresh = found
    .filter((p) => !has(p.handle))
    .map((p) => {
      const img = p.featured_image ? `${p.featured_image.startsWith('//') ? 'https:' : ''}${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=240` : '';
      const price = p.price > 0 ? `${p.price_varies ? 'From ' : ''}${window.TAT?.formatMoney ? window.TAT.formatMoney(p.price) : `$${(p.price / 100).toFixed(2)}`}` : '';
      // Same rule as snippets/product-state for the legacy tag (a purchase-mode metafield isn't in the product JSON).
      const mode = (p.tags || []).includes('noaddtocart') ? 'enquire' : '';
      return { handle: p.handle, title: p.title, vendor: p.vendor, url: p.url, image: img, price, mode, added: Date.now() };
    });
  save([...fresh, ...items]);
  if (found.length) track('shortlist_open_shared', { shortlist_count: found.length });
  if (found.length) requestAnimationFrame(openShortlist);
} else {
  render();
}

// The full-screen shortlist (assets/shortlist-modal.js) works through this, so there is one copy of the list in memory.
window.TAT = window.TAT || {};
window.TAT.shortlist = {
  get items() {
    return items;
  },
  save,
  recent: () => read(RECENT_KEY).filter((r) => !has(r.handle)),
  generalNote,
  setGeneralNote: (value) => {
    try {
      localStorage.setItem(NOTE_KEY, value);
    } catch {}
  },
  line,
  track,
  gaItems,
  toast,
};
document.dispatchEvent(new CustomEvent('shortlist:ready'));
if (location.hash === '#shortlist') openShortlist();
window.addEventListener('hashchange', () => location.hash === '#shortlist' && openShortlist());
